import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, toast, useConfirm } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Eye, RefreshCw, RefreshCcwDot } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CLIENT_TYPES,
  FEED_TYPES,
  FEED_TYPE_LABELS,
  useApi,
  type ClientDTO,
  type FeedType,
} from "../../api";
import { crudEditor, multiSelectField, selectField, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * Wholesale clients.
 *
 * "Sync" pushes the client and its addresses to Shopify as a company /
 * customer pair; the returned GIDs are what later orders are attributed with,
 * so a client that was never synced can't be used on a draft order.
 */
export function ClientsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.CONFIG_CLIENT);
  const title = t("navigation.item.clients", "Clients");
  const [selected, setSelected] = useState<ClientDTO[]>([]);
  const [syncing, setSyncing] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["client"],
    queryFn: () => api.clients.findAll(),
    enabled: allowed,
  });

  const rows = data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["client"] });

  const cols = useMemo(() => {
    const c = defineColumns<ClientDTO>();
    return [
      c.text("type", (row) => row.type, {
        headerKey: "column.type",
        filter: "select",
        priority: 3,
      }),
      c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
      c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
      c.text("contact_first_name", (row) => row.contact_first_name, {
        headerKey: "column.contact.first-name",
        priority: 4,
      }),
      c.text("contact_last_name", (row) => row.contact_last_name, {
        headerKey: "column.contact.last-name",
        priority: 4,
      }),
      c.text("contact_email", (row) => row.contact_email, {
        headerKey: "column.contact.email",
        priority: 2,
      }),
      c.text("contact_phone", (row) => row.contact_phone, {
        headerKey: "column.contact.phone",
        priority: 5,
      }),
      c.custom(
        "currency",
        (row) => row.currency,
        (row) => (
          <Badge variant={row.currency === "USD" ? "success" : "info"}>{row.currency}</Badge>
        ),
        { headerKey: "column.currency", filter: "select", priority: 3 },
      ),
      c.text(
        "feed_types",
        (row) => (row.feed_types ?? []).map((entry) => FEED_TYPE_LABELS[entry]).join(", "),
        { headerKey: "column.feed-types", filter: "select", priority: 5 },
      ),
    ];
  }, []);

  const editor = useMemo(
    () =>
      crudEditor<ClientDTO>({
        client: api.clients,
        refresh,
        t,
        fields: {
          type: selectField(
            (row) => row.type,
            (row, value) => ({ ...row, type: value as ClientDTO["type"] }),
            CLIENT_TYPES,
          ),
          code: textField(
            (row) => row.code,
            (row, value) => ({ ...row, code: value }),
            true,
          ),
          name: textField(
            (row) => row.name,
            (row, value) => ({ ...row, name: value }),
            true,
          ),
          contact_first_name: textField(
            (row) => row.contact_first_name,
            (row, value) => ({ ...row, contact_first_name: value }),
            true,
          ),
          contact_last_name: textField(
            (row) => row.contact_last_name,
            (row, value) => ({ ...row, contact_last_name: value }),
            true,
          ),
          contact_email: {
            kind: "email",
            get: (row) => row.contact_email ?? "",
            set: (row, value) => ({ ...row, contact_email: value }),
            required: true,
          },
          contact_phone: textField(
            (row) => row.contact_phone,
            (row, value) => ({ ...row, contact_phone: value }),
          ),
          currency: selectField(
            (row) => row.currency,
            (row, value) => ({ ...row, currency: value as ClientDTO["currency"] }),
            ["USD", "CAD"],
          ),
          feed_types: multiSelectField(
            (row) => row.feed_types ?? [],
            (row, values) => ({ ...row, feed_types: values as FeedType[] }),
            FEED_TYPES.map((entry) => ({ value: entry, label: FEED_TYPE_LABELS[entry] })),
          ),
        },
        newRow: () => ({
          id: 0,
          type: "CUSTOMER",
          code: "",
          name: "",
          contact_first_name: "",
          contact_last_name: "",
          contact_email: "",
          contact_phone: "",
          currency: "CAD",
          addresses: [],
          notes: [],
          feed_types: [],
        }),
        validate: (row) => {
          const errors: Record<string, string> = {};
          const required = t("form.validation.field.required", "Value is required");
          const unique = t("form.validation.field.unique", "Value already exists");
          if (!row.code.trim()) errors.code = required;
          else if (rows.some((entry) => entry.id !== row.id && entry.code === row.code)) {
            errors.code = unique;
          }
          if (!row.name.trim()) errors.name = required;
          else if (rows.some((entry) => entry.id !== row.id && entry.name === row.name)) {
            errors.name = unique;
          }
          if (!row.contact_first_name?.trim()) errors.contact_first_name = required;
          if (!row.contact_last_name?.trim()) errors.contact_last_name = required;
          if (!row.contact_email?.trim()) errors.contact_email = required;
          return Object.keys(errors).length > 0 ? errors : null;
        },
      }),
    [api, rows, t],
  );

  const syncAll = async () => {
    const ok = await confirm({
      title: t("menu.item.sync-all", "Sync all"),
      message: t("dialog.confirm.sync-clients.text", "Push every client to Shopify?"),
    });
    if (!ok) return;
    setSyncing(true);
    try {
      await api.clients.syncAll();
      toast.success(t("notification.sync.complete", "Sync completed"));
      await refresh();
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Sync failed"));
    } finally {
      setSyncing(false);
    }
  };

  const syncSelected = async () => {
    setSyncing(true);
    try {
      for (const client of selected) {
        await api.clients.sync(client.id);
      }
      toast.success(t("notification.sync.complete", "Sync completed"));
      await refresh();
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Sync failed"));
    } finally {
      setSyncing(false);
    }
  };

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<ClientDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/config/client"
        loading={isLoading}
        selectable
        onSelectionChange={setSelected}
        editor={editor}
        actions={[
          {
            label: t("menu.item.sync-all", "Sync all"),
            icon: <RefreshCw className="size-4" />,
            write: true,
            disabled: syncing,
            onClick: () => void syncAll(),
          },
          {
            label: t("menu.item.sync-selected", "Sync selected"),
            icon: <RefreshCcwDot className="size-4" />,
            write: true,
            requiresSelection: true,
            disabled: syncing,
            onClick: () => void syncSelected(),
          },
        ]}
        onRowClick={(row) =>
          void navigate({ to: "/config/client/$id", params: { id: String(row.id) } })
        }
        rowActions={(row) => (
          <Button
            size="sm"
            variant="ghost"
            title={t("tab.addresses", "Addresses")}
            className="size-7 p-0"
            icon={<Eye className="size-3.5" />}
            onClick={() => void navigate({ to: "/config/client/$id", params: { id: String(row.id) } })}
          />
        )}
      />
    </>
  );
}
